<script lang="ts">
  import { fetchUnitValidation } from '$lib/api';
  import { onMount } from 'svelte';
  import {
    CheckCircle2,
    AlertCircle,
    AlertTriangle,
    Download,
    Clock,
    HardDrive,
    ListChecks
  } from 'lucide-svelte';
  import { router } from '$lib/router';
  import RelayMapPanel from './RelayMapPanel.svelte';
  import { uiState } from '../state/ui.svelte';
  import { unitsQuery } from '../queries/units';
  import { zonesQuery } from '../queries/zones';
  import { roomsQuery } from '../queries/rooms';
  import { loadsQuery } from '../queries/loads';
  import { inputDevicesQuery } from '../queries/input_devices';
  import { rcusQuery } from '../queries/rcus';
  import { dinModulesForUnitQuery } from '../queries/din_modules';
  import { rcuChannelsQuery } from '../queries/rcu_channels';
  import { scenesQuery } from '../queries/scenes';
  import { unitSceneLoadsQuery } from '../queries/scene_loads';
  import { ledScenesQuery } from '../queries/led_scenes';
  import { projectFilesQuery } from '../queries/project_files';
  import { computeUnitWorkflow } from '../workflow';

  const units = unitsQuery();
  const zones = zonesQuery();
  const rooms = roomsQuery();
  const loads = loadsQuery();
  const devices = inputDevicesQuery();
  const rcus = rcusQuery();
  const dinModules = dinModulesForUnitQuery();
  const rcuChannels = rcuChannelsQuery();
  const scenes = scenesQuery();
  const sceneLoads = unitSceneLoadsQuery();
  const ledScenes = ledScenesQuery();
  const projectFiles = projectFilesQuery();

  let unit = $derived((units.data ?? []).find((u) => u.id === router.unitId) ?? null);
  let unitZones = $derived(zones.data ?? []);
  let unitRooms = $derived(rooms.data ?? []);
  let unitLoads = $derived(loads.data ?? []);
  let unitDevices = $derived(devices.data ?? []);
  let unitInputKeys = $derived((devices.data ?? []).flatMap((d) => d.keys ?? []));
  let unitChannels = $derived(
    (rcuChannels.data ?? []).map((c) => ({ load: c.load, object_id: c.rcu }))
  );

  let validation = $state<Record<string, unknown> | null>(null);
  let validationLoading = $state(false);
  let validationError = $state<string | null>(null);

  let workflow = $derived(
    computeUnitWorkflow({
      unit: unit,
      zones: unitZones,
      rooms: unitRooms,
      loads: unitLoads,
      inputDevices: unitDevices,
      inputKeys: unitInputKeys,
      rcus: rcus.data ?? [],
      dinModules: dinModules.data ?? [],
      channels: unitChannels,
      scenes: scenes.data ?? [],
      sceneLoads: sceneLoads.data ?? []
    })
  );

  async function runValidation() {
    if (!unit) return;
    validationLoading = true;
    validationError = null;
    try {
      validation = (await fetchUnitValidation(unit.id)) as Record<string, unknown>;
      uiState.validationResults = (validation?.errors as Array<unknown>) || [];
    } catch (e: unknown) {
      validationError = (e as Error).message || 'Validation request failed';
    } finally {
      validationLoading = false;
    }
  }

  onMount(() => {
    if (unit) runValidation();
  });

  $effect(() => {
    if (unit) runValidation();
  });

  const checklistItems = [
    {
      id: 'zones-rooms',
      label: 'Zones & rooms defined',
      check: () => unitZones.length > 0 && unitRooms.length > 0,
      section: 'zones'
    },
    {
      id: 'loads-defined',
      label: 'Loads configured',
      check: () => unitLoads.length > 0,
      section: 'loads'
    },
    {
      id: 'loads-assigned',
      label: 'All loads assigned to channels',
      check: () => workflow.entities.unassignedLoads.length === 0,
      section: 'hardware'
    },
    {
      id: 'hardware-present',
      label: 'RCU hardware present',
      check: () => workflow.entities.rcus.length > 0,
      section: 'hardware'
    },
    {
      id: 'addresses-unique',
      label: 'No duplicate keypad addresses',
      check: () => workflow.entities.duplicateAddresses.length === 0,
      section: 'keypads'
    },
    {
      id: 'keypads-programmed',
      label: 'All keypads have actions',
      check: () => workflow.entities.unprogrammedDevices.length === 0,
      section: 'keypads'
    },
    {
      id: 'scenes-ready',
      label: 'All scenes have load values',
      check: () => workflow.entities.emptyScenes.length === 0,
      section: 'keypads'
    },
    {
      id: 'led-synced',
      label: 'LED mappings generated',
      check: () => (ledScenes.data ?? []).length > 0,
      section: 'led-logic'
    },
    {
      id: 'validation-passed',
      label: 'Backend validation passed',
      check: () => validation && (validation.errors as Array<unknown>)?.length === 0,
      section: 'hardware'
    }
  ];

  let expandedIssues = $state(false);
</script>

<div class="space-y-4">
  <section class="border border-border-default rounded p-3">
    <div class="flex items-center gap-2 mb-3">
      <ListChecks class="w-4 h-4 text-text-secondary" />
      <h3 class="text-base font-medium text-text-primary">Commissioning Checklist</h3>
    </div>
    <div class="space-y-1">
      {#each checklistItems as item}
        {@const passed = item.check()}
        <div
          class="flex items-center justify-between py-1.5 px-2 rounded h-8 {passed
            ? 'bg-bg-rail'
            : 'bg-amber-50/50'}"
        >
          <div class="flex items-center gap-2">
            {#if passed}
              <CheckCircle2 class="w-3.5 h-3.5 text-state-ok shrink-0" />
              <span class="text-sm text-text-secondary">{item.label}</span>
            {:else}
              <AlertCircle class="w-3.5 h-3.5 text-state-warn shrink-0" />
              <span class="text-sm text-text-primary font-medium">{item.label}</span>
            {/if}
          </div>
          {#if !passed}
            <span class="text-2xs text-state-warn font-medium">Needs action</span>
          {:else}
            <span class="text-2xs text-state-ok font-medium">Passed</span>
          {/if}
        </div>
      {/each}
    </div>
  </section>

  <section class="border border-border-default rounded p-3">
    <div class="flex items-center justify-between mb-3">
      <div class="flex items-center gap-2">
        <AlertTriangle class="w-4 h-4 text-text-secondary" />
        <h3 class="text-base font-medium text-text-primary">Validation</h3>
      </div>
      <button
        type="button"
        onclick={runValidation}
        disabled={validationLoading || !unit}
        class="text-xs px-2 py-1 border border-border-default rounded text-text-secondary hover:bg-bg-hover disabled:opacity-40"
      >
        {validationLoading ? 'Running…' : 'Re-run'}
      </button>
    </div>

    {#if validationLoading}
      <div class="flex items-center space-x-2 text-sm text-text-muted py-4">
        <Clock class="w-4 h-4 animate-spin" />
        <span>Running validation…</span>
      </div>
    {:else if validationError}
      <div class="flex items-center space-x-2 text-sm text-red-600 py-2">
        <AlertCircle class="w-4 h-4" />
        <span>{validationError}</span>
      </div>
    {:else if validation}
      {#if validation.errors?.length > 0}
        <div class="space-y-2">
          <p class="text-sm font-medium text-red-700">
            {validation.errors.length} error{validation.errors.length !== 1 ? 's' : ''} found
          </p>
          {#if expandedIssues || validation.errors.length <= 5}
            {#each validation.errors as err}
              <div
                class="flex items-start space-x-2 text-sm text-red-700 bg-red-50 rounded-md px-3 py-2"
              >
                <AlertCircle class="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{err.message || err}</span>
              </div>
            {/each}
          {/if}
          {#if validation.errors.length > 5}
            <button
              onclick={() => (expandedIssues = !expandedIssues)}
              class="text-xs text-blue-600 hover:underline"
            >
              {expandedIssues ? 'Show less' : 'Show all {validation.errors.length} errors'}
            </button>
          {/if}
        </div>
      {:else}
        <div
          class="flex items-center space-x-2 text-sm text-green-700 bg-green-50 rounded-md px-3 py-2"
        >
          <CheckCircle2 class="w-4 h-4" />
          <span>All validations passed — ready for export</span>
        </div>
      {/if}
    {:else}
      <p class="text-sm text-text-muted">No validation data yet.</p>
    {/if}
  </section>

  {#if unit}
    <RelayMapPanel unitId={unit.id} />
  {/if}

  <section class="border border-border-default rounded p-3">
    <div class="flex items-center gap-2 mb-3">
      <Download class="w-4 h-4 text-text-secondary" />
      <h3 class="text-base font-medium text-text-primary">Export & Firmware</h3>
    </div>
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div class="border border-border-default rounded p-2">
        <p class="text-2xs text-text-muted uppercase tracking-wide font-medium mb-0.5">Firmware</p>
        <p class="text-sm text-text-secondary">
          {workflow.entities.rcus.length} RCU{workflow.entities.rcus.length !== 1 ? 's' : ''} to flash
        </p>
      </div>
      <div class="border border-border-default rounded p-2">
        <p class="text-2xs text-text-muted uppercase tracking-wide font-medium mb-0.5">Reports</p>
        <p class="text-sm text-text-secondary">Logic sheet, load chart, electrical layout</p>
      </div>
      <div class="border border-border-default rounded p-2">
        <p class="text-2xs text-text-muted uppercase tracking-wide font-medium mb-0.5">
          Project Files
        </p>
        <p class="text-sm text-text-secondary">
          {projectFiles.data?.length ?? 0} file{(projectFiles.data?.length ?? 0) !== 1 ? 's' : ''} uploaded
        </p>
      </div>
      <div class="border border-border-default rounded p-2">
        <p class="text-2xs text-text-muted uppercase tracking-wide font-medium mb-0.5">
          Workflow Issues
        </p>
        {#if workflow.attentionCount > 0}
          <p class="text-sm text-state-warn font-medium">
            {workflow.attentionCount} issue{workflow.attentionCount !== 1 ? 's' : ''}
          </p>
        {:else}
          <p class="text-sm text-state-ok">No issues</p>
        {/if}
      </div>
    </div>
  </section>

  <section class="border border-border-default rounded p-3">
    <div class="flex items-center gap-2 mb-3">
      <HardDrive class="w-4 h-4 text-text-secondary" />
      <h3 class="text-base font-medium text-text-primary">Unit Summary</h3>
    </div>
    <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
      <div class="text-center p-2 bg-bg-rail rounded">
        <p class="text-base font-medium text-text-primary font-mono">{unitZones.length}</p>
        <p class="text-2xs text-text-muted">Zones</p>
      </div>
      <div class="text-center p-2 bg-bg-rail rounded">
        <p class="text-base font-medium text-text-primary font-mono">{unitRooms.length}</p>
        <p class="text-2xs text-text-muted">Rooms</p>
      </div>
      <div class="text-center p-2 bg-bg-rail rounded">
        <p class="text-base font-medium text-text-primary font-mono">{unitLoads.length}</p>
        <p class="text-2xs text-text-muted">Loads</p>
      </div>
      <div class="text-center p-2 bg-bg-rail rounded">
        <p class="text-base font-medium text-text-primary font-mono">
          {workflow.entities.rcus.length}
        </p>
        <p class="text-2xs text-text-muted">RCUs</p>
      </div>
      <div class="text-center p-2 bg-bg-rail rounded">
        <p class="text-base font-medium text-text-primary font-mono">
          {workflow.entities.channels.length}
        </p>
        <p class="text-2xs text-text-muted">Channels</p>
      </div>
      <div class="text-center p-2 bg-bg-rail rounded">
        <p class="text-base font-medium text-text-primary font-mono">{unitDevices.length}</p>
        <p class="text-2xs text-text-muted">Devices</p>
      </div>
      <div class="text-center p-2 bg-bg-rail rounded">
        <p class="text-base font-medium text-text-primary font-mono">
          {workflow.entities.scenes.length}
        </p>
        <p class="text-2xs text-text-muted">Scenes</p>
      </div>
      <div class="text-center p-2 bg-bg-rail rounded">
        <p class="text-base font-medium text-text-primary font-mono">
          {workflow.entities.dinModules.length}
        </p>
        <p class="text-2xs text-text-muted">DIN Modules</p>
      </div>
    </div>
  </section>
</div>
